// src/components/timetable/modern/ModernArrangementsView.tsx
//
// Ported from the mobile app's ArrangementsView.tsx. RN's bottom-sheet Modal
// becomes a PrimeReact Dialog; RN's Alert.alert becomes a PrimeReact Toast +
// a small confirm Dialog, matching TimeSlotsManager.tsx's delete-confirm
// pattern. Business logic (slot/teacher resolution, id lookups) is unchanged.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import {
  AddArrangementPayload,
  AffectedSlot,
  ArrangementPeriod,
  C,
  ClassTimetable,
  DayOfWeek,
  TeacherArrangement,
  formatTime,
} from './modernTimetableTypes';

// ─── Add Arrangement Modal ────────────────────────────────────────────────────

interface AddArrangementModalProps {
  visible:           boolean;
  onClose:           () => void;
  onSubmit:          (payload: AddArrangementPayload) => Promise<void>;
  submitting:        boolean;
  timetableData:     ClassTimetable[];
  periodRow:         ArrangementPeriod | null;
  absentTeacherName: string | null;
}

const ModalPicker: React.FC<{
  label:     string;
  options:   { label: string; value: string }[];
  selected:  string;
  onSelect:  (v: string) => void;
  emptyMsg?: string;
}> = ({ label, options, selected, onSelect, emptyMsg }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, marginBottom: 8 }}>{label}</div>
    {options.length === 0 ? (
      <div style={{ fontSize: 12.5, color: C.textLight, fontStyle: 'italic' }}>{emptyMsg ?? 'No options available'}</div>
    ) : (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((opt) => {
          const sel = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              style={{
                background: sel ? C.affected : '#F3F4F6',
                color: sel ? '#fff' : C.textSecondary,
                border: `1px solid ${sel ? C.affected : C.border}`,
                borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: sel ? 700 : 500, cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

const AddArrangementModal: React.FC<AddArrangementModalProps> = ({
  visible, onClose, onSubmit, submitting,
  timetableData, periodRow, absentTeacherName,
}) => {
  const [selectedSlotKey, setSelectedSlotKey] = useState('');
  const [arrangedTeacher, setArrangedTeacher] = useState('');

  useEffect(() => {
    if (visible) {
      setSelectedSlotKey('');
      setArrangedTeacher('');
    }
  }, [visible, absentTeacherName, periodRow]);

  const affectedSlots = useMemo(() => {
    if (!periodRow || !absentTeacherName) return [];
    return periodRow.absentTeachers.find((t) => t.name === absentTeacherName)?.affectedSlots ?? [];
  }, [periodRow, absentTeacherName]);

  const slotOptions = useMemo(() =>
    affectedSlots.map((slot, i) => ({
      label: `${slot.className}${slot.section ? ' · ' + slot.section : ''} — ${slot.subject}`,
      value: String(i),
    })),
  [affectedSlots]);

  const selectedSlot = selectedSlotKey !== '' ? affectedSlots[parseInt(selectedSlotKey, 10)] : null;

  const vacantOptions = useMemo(() =>
    (periodRow?.vacantTeachers ?? []).map((t) => ({ label: t, value: t })),
  [periodRow]);

  const primaryTeacherId = useMemo(() => {
    if (!absentTeacherName) return null;
    for (const cls of timetableData) {
      const item = cls.schedule.find((s) => s.teacher_name === absentTeacherName && s.teacher_id);
      if (item) return item.teacher_id;
    }
    return null;
  }, [absentTeacherName, timetableData]);

  const arrangedTeacherId = useMemo(() => {
    if (!arrangedTeacher) return null;
    for (const cls of timetableData) {
      const item = cls.schedule.find((s) => s.teacher_name === arrangedTeacher && s.teacher_id);
      if (item) return item.teacher_id;
    }
    return null;
  }, [arrangedTeacher, timetableData]);

  const resolvedSlot = useMemo(() => {
    if (!selectedSlot || !absentTeacherName) return null;
    for (const cls of timetableData) {
      if (cls.class_name !== selectedSlot.className) continue;
      const item = cls.schedule.find((s) =>
        s.teacher_name === absentTeacherName &&
        s.subject_name === selectedSlot.subject &&
        (selectedSlot.section === null ? !s.section : s.section === selectedSlot.section)
      );
      if (item) return { class_id: cls.class_id, subject_id: item.subject_id, section: item.section };
    }
    return null;
  }, [selectedSlot, absentTeacherName, timetableData]);

  const canSubmit =
    !!selectedSlot && !!arrangedTeacher &&
    !!primaryTeacherId && !!arrangedTeacherId && !!resolvedSlot && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !resolvedSlot || !primaryTeacherId || !arrangedTeacherId) return;
    await onSubmit({
      class_id:            resolvedSlot.class_id,
      subject_id:          resolvedSlot.subject_id!,
      primary_teacher_id:  primaryTeacherId,
      arranged_teacher_id: arrangedTeacherId,
      ...(resolvedSlot.section ? { section: resolvedSlot.section } : {}),
    });
  };

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="pi pi-sitemap" style={{ color: C.affected }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary }}>New Arrangement</div>
            {absentTeacherName && <div style={{ fontSize: 11.5, color: C.absent, marginTop: 1 }}>Absent: {absentTeacherName}</div>}
          </div>
        </div>
      }
      visible={visible}
      onHide={onClose}
      style={{ width: '90vw', maxWidth: '480px', borderRadius: '20px' }}
      contentStyle={{ padding: '20px' }}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
    >
      <ModalPicker
        label="Affected Class & Subject"
        options={slotOptions}
        selected={selectedSlotKey}
        onSelect={(v) => { setSelectedSlotKey(v); setArrangedTeacher(''); }}
        emptyMsg="No affected classes found for this teacher"
      />

      {selectedSlotKey !== '' && (
        <ModalPicker
          label="Replacement Teacher (vacant this period)"
          options={vacantOptions}
          selected={arrangedTeacher}
          onSelect={setArrangedTeacher}
          emptyMsg="No vacant teachers available this period"
        />
      )}

      {selectedSlot && arrangedTeacher && (
        <div style={{ display: 'flex', gap: 8, background: C.affectedBg, border: `1px solid ${C.affectedBorder}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <i className="pi pi-arrow-right-arrow-left" style={{ color: C.affected, fontSize: 14, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: C.textSecondary }}>
            <strong style={{ color: C.affected }}>{arrangedTeacher}</strong> will cover{' '}
            <strong style={{ color: C.textPrimary }}>
              {selectedSlot.className}{selectedSlot.section ? ` · ${selectedSlot.section}` : ''} — {selectedSlot.subject}
            </strong>
          </div>
        </div>
      )}

      {selectedSlotKey !== '' && vacantOptions.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.absentBg, border: `1px solid ${C.absentBorder}`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
          <i className="pi pi-exclamation-triangle" style={{ color: C.absent, fontSize: 13 }} />
          <span style={{ fontSize: 12.5, color: C.absent }}>All teachers are busy this period. No replacements available.</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <GlobalButton
          label={submitting ? 'Saving...' : 'Save Arrangement'}
          icon="pi pi-check-circle"
          onClick={handleSubmit}
          variant="primary"
          size="medium"
          disabled={!canSubmit}
          loading={submitting}
        />
      </div>
    </Dialog>
  );
};

// ─── Small chip components ─────────────────────────────────────────────────────

const AffectedChip: React.FC<{ slot: AffectedSlot }> = ({ slot }) => (
  <div style={{ background: C.affectedBg, borderRadius: 8, border: `1px solid ${C.affectedBorder}`, padding: '5px 8px', marginBottom: 5 }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, color: C.affected }}>{slot.className}{slot.section ? ` · ${slot.section}` : ''}</div>
    <div style={{ marginTop: 3, background: 'rgba(13,148,136,0.1)', borderRadius: 5, display: 'inline-block', padding: '2px 5px' }}>
      <span style={{ fontSize: 10.5, color: C.affected, opacity: 0.85 }}>{slot.subject}</span>
    </div>
  </div>
);

const VacantChip: React.FC<{ name: string; isLive: boolean; isArranged?: boolean }> = ({ name, isLive, isArranged }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', borderRadius: 7, border: '1px solid',
      padding: '4px 8px', marginBottom: 5, width: '100%',
      background: isArranged ? 'rgba(245,158,11,0.13)' : isLive ? 'rgba(22,163,74,0.12)' : C.vacantBg,
      borderColor: isArranged ? 'rgba(245,158,11,0.5)' : isLive ? C.live : 'rgba(16,185,129,0.3)',
    }}
  >
    {isArranged && <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 10, color: '#92400E', marginRight: 3 }} />}
    <span style={{ fontSize: 11.5, fontWeight: 700, color: isArranged ? '#92400E' : isLive ? C.live : C.vacant, flex: 1 }}>{name}</span>
  </div>
);

const AbsentChip: React.FC<{ name: string }> = ({ name }) => (
  <div style={{ flex: 1, background: C.absentBg, borderRadius: 7, border: `1px solid ${C.absentBorder}`, padding: '4px 8px' }}>
    <span style={{ fontSize: 11.5, fontWeight: 700, color: C.absent }}>{name}</span>
  </div>
);

const ArrangementChip: React.FC<{ arrangement: TeacherArrangement; onDelete: (a: TeacherArrangement) => void; deleting: boolean }> = ({ arrangement, onDelete, deleting }) => (
  <div style={{ background: C.affectedBg, borderRadius: 8, border: `1px solid ${C.affectedBorder}`, padding: '6px 8px', marginBottom: 5, position: 'relative' }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
      <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 11, color: C.affected, marginRight: 3 }} />
      <span style={{ fontSize: 11.5, fontWeight: 700, color: C.affected, flex: 1 }}>{arrangement.arranged_teacher_name}</span>
    </div>
    <div style={{ fontSize: 11, color: C.affected, opacity: 0.9, marginBottom: 1 }}>
      {arrangement.class_name}{arrangement.section ? ` · ${arrangement.section}` : ''} — {arrangement.subject_name}
    </div>
    <div style={{ fontSize: 10.5, color: C.textLight, fontStyle: 'italic' }}>covers {arrangement.primary_teacher_name}</div>
    <button
      type="button"
      onClick={() => onDelete(arrangement)}
      disabled={deleting}
      style={{ position: 'absolute', top: 5, right: 6, background: 'none', border: 'none', cursor: deleting ? 'default' : 'pointer' }}
    >
      <i className={deleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'} style={{ fontSize: 12, color: C.absent }} />
    </button>
  </div>
);

const AddBtn: React.FC<{ onPress: () => void }> = ({ onPress }) => (
  <button
    type="button"
    onClick={onPress}
    style={{ display: 'flex', alignItems: 'center', gap: 3, background: C.affectedBg, borderRadius: 7, border: `1px solid ${C.affectedBorder}`, padding: '4px 7px', cursor: 'pointer' }}
  >
    <i className="pi pi-plus-circle" style={{ fontSize: 11, color: C.affected }} />
    <span style={{ fontSize: 11.5, fontWeight: 700, color: C.affected }}>Add</span>
  </button>
);

// ─── Table header + row ─────────────────────────────────────────────────────────

const TableHeader: React.FC = () => (
  <div className="mav-row" style={{ borderBottom: `1px solid ${C.borderDk}`, height: 50 }}>
    <div className="mav-period-cell" style={{ background: '#F0F4FF' }}>
      <i className="pi pi-clock" style={{ fontSize: 12, color: C.primary }} />
      <span style={{ color: C.primary, fontSize: 11.5, fontWeight: 700 }}>Period</span>
    </div>
    <div className="mav-divider" />
    <div className="mav-col" style={{ background: 'rgba(16,185,129,0.07)' }}>
      <i className="pi pi-user" style={{ fontSize: 12, color: C.vacant }} />
      <span style={{ color: C.vacant, fontSize: 11.5, fontWeight: 700 }}>Vacant</span>
    </div>
    <div className="mav-divider" />
    <div className="mav-col" style={{ background: 'rgba(220,38,38,0.06)' }}>
      <i className="pi pi-times-circle" style={{ fontSize: 12, color: C.absent }} />
      <span style={{ color: C.absent, fontSize: 11.5, fontWeight: 700 }}>Absent</span>
    </div>
    <div className="mav-divider" />
    <div className="mav-col" style={{ background: C.affectedBg }}>
      <i className="pi pi-exclamation-circle" style={{ fontSize: 12, color: C.affected }} />
      <span style={{ color: C.affected, fontSize: 11.5, fontWeight: 700 }}>Affected / Arranged</span>
    </div>
  </div>
);

const ArrangementRow: React.FC<{
  row:               ArrangementPeriod;
  isLast:            boolean;
  savedArrangements: TeacherArrangement[];
  onAddPress:        (absentName: string | null, row: ArrangementPeriod) => void;
  onDeleteArrangement: (a: TeacherArrangement) => void;
  deletingId:        string | null;
}> = ({ row, isLast, savedArrangements, onAddPress, onDeleteArrangement, deletingId }) => {

  if (row.isBreak) {
    return (
      <div className="mav-row" style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}` }}>
        <div className="mav-period-cell" style={{ background: 'rgba(245,158,11,0.06)' }}>
          <span style={{ color: C.break, fontWeight: 700, fontSize: 12.5, textAlign: 'center' }}>{row.period}</span>
          <span style={{ color: C.textLight, fontSize: 10.5, marginTop: 1 }}>{formatTime(row.startTime)}</span>
          <span style={{ color: C.textLight, fontSize: 10.5 }}>{formatTime(row.endTime)}</span>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.05)' }}>
          <i className="pi pi-shopping-bag" style={{ color: C.break, fontSize: 15 }} />
          <span style={{ color: C.break, fontWeight: 700, marginLeft: 6, fontSize: 12.5 }}>Break</span>
        </div>
      </div>
    );
  }

  const arrangedTeacherNames = new Set(
    savedArrangements
      .filter((a) => row.absentTeachers.some((t) => t.name === a.primary_teacher_name))
      .map((a) => a.arranged_teacher_name)
  );

  const hasAnyContent = row.absentTeachers.some((t) =>
    t.affectedSlots.length > 0 || savedArrangements.some((a) => a.primary_teacher_name === t.name)
  );

  return (
    <div className="mav-row" style={{ borderBottom: isLast ? 'none' : `1px solid ${C.border}`, background: row.isLive ? 'rgba(22,163,74,0.03)' : C.cardBg }}>
      <div className="mav-period-cell" style={{ background: row.isLive ? 'rgba(22,163,74,0.06)' : '#F8F9FC' }}>
        {row.isLive && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 2 }}>
            <span className="mav-live-dot" />
            <span style={{ color: C.live, fontSize: 9, fontWeight: 700 }}>Now</span>
          </div>
        )}
        <span style={{ color: row.isLive ? C.live : C.primary, fontWeight: 700, fontSize: 12.5, textAlign: 'center' }}>{row.period}</span>
        <span style={{ color: C.textLight, fontSize: 10.5, marginTop: 1 }}>{formatTime(row.startTime)}</span>
        <span style={{ color: C.textLight, fontSize: 10.5 }}>{formatTime(row.endTime)}</span>
      </div>

      <div className="mav-divider" />

      <div className="mav-col" style={{ alignItems: 'stretch' }}>
        {row.vacantTeachers.length === 0 ? (
          <span style={{ color: C.textLight, fontStyle: 'italic', fontSize: 11.5, marginTop: 4 }}>All busy</span>
        ) : (
          row.vacantTeachers.map((name) => (
            <VacantChip key={name} name={name} isLive={row.isLive} isArranged={arrangedTeacherNames.has(name)} />
          ))
        )}
      </div>

      <div className="mav-divider" />

      <div className="mav-col" style={{ alignItems: 'stretch' }}>
        {row.absentTeachers.length === 0 ? (
          <span style={{ color: C.textLight, fontStyle: 'italic', fontSize: 11.5, marginTop: 4 }}>All present</span>
        ) : (
          row.absentTeachers.map((t) => (
            <div key={t.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginBottom: 5, width: '100%' }}>
              <AbsentChip name={t.name} />
              <AddBtn onPress={() => onAddPress(t.name, row)} />
            </div>
          ))
        )}
      </div>

      <div className="mav-divider" />

      <div className="mav-col" style={{ alignItems: 'stretch' }}>
        {!hasAnyContent ? (
          <span style={{ color: C.textLight, fontStyle: 'italic', fontSize: 11.5, marginTop: 4 }}>—</span>
        ) : (
          row.absentTeachers.map((t, ti) => {
            const teacherArrangements = savedArrangements.filter((a) => a.primary_teacher_name === t.name);
            if (t.affectedSlots.length === 0 && teacherArrangements.length === 0) return null;
            return (
              <div key={t.name} style={{ width: '100%', borderTop: ti > 0 ? `1px solid ${C.border}` : 'none', marginTop: ti > 0 ? 6 : 0, paddingTop: ti > 0 ? 6 : 0 }}>
                {t.affectedSlots.map((slot, i) => (
                  <AffectedChip key={`${slot.teacherName}-${i}`} slot={slot} />
                ))}
                {teacherArrangements.length > 0 && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', borderTop: `1px solid ${C.affectedBorder}`, marginTop: 4, marginBottom: 4, paddingTop: 4 }}>
                      <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 10, color: C.affected }} />
                      <span style={{ color: C.affected, marginLeft: 3, fontSize: 11, fontWeight: 700 }}>Arranged</span>
                    </div>
                    {teacherArrangements.map((a) => (
                      <ArrangementChip key={a.id} arrangement={a} onDelete={onDeleteArrangement} deleting={deletingId === a.id} />
                    ))}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Summary bar ──────────────────────────────────────────────────────────────

const SummaryBar: React.FC<{
  periods: ArrangementPeriod[];
  totalArranged: number;
  onAddGlobal: () => void;
}> = ({ periods, totalArranged, onAddGlobal }) => {
  const nonBreak    = periods.filter((p) => !p.isBreak);
  const totalAbsent = new Set(nonBreak.flatMap((p) => p.absentTeachers.map((t) => t.name))).size;
  const totalVacant = new Set(nonBreak.flatMap((p) => p.vacantTeachers)).size;
  const totalAffect = new Set(nonBreak.flatMap((p) => p.absentTeachers.flatMap((t) => t.affectedSlots.map((s) => `${s.className}-${s.section}`)))).size;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.cardBg, borderRadius: 12, border: `1px solid ${C.border}`, padding: '10px 14px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: 3.5, background: C.vacant, display: 'inline-block' }} />
        <span style={{ color: C.vacant, fontWeight: 700, fontSize: 12.5 }}>{totalVacant} Vacant</span>
      </div>
      <div style={{ width: 1, height: 14, background: C.border }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: 3.5, background: C.absent, display: 'inline-block' }} />
        <span style={{ color: C.absent, fontWeight: 700, fontSize: 12.5 }}>{totalAbsent} Absent</span>
      </div>
      <div style={{ width: 1, height: 14, background: C.border }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ width: 7, height: 7, borderRadius: 3.5, background: C.affected, display: 'inline-block' }} />
        <span style={{ color: C.affected, fontWeight: 700, fontSize: 12.5 }}>{totalAffect} Affected</span>
      </div>
      {totalArranged > 0 && (
        <>
          <div style={{ width: 1, height: 14, background: C.border }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="pi pi-arrow-right-arrow-left" style={{ fontSize: 11, color: C.affected }} />
            <span style={{ color: C.affected, fontWeight: 700, fontSize: 12.5 }}>{totalArranged} Arranged</span>
          </div>
        </>
      )}
      <div style={{ flex: 1 }} />
      <GlobalButton label="Arrange" icon="pi pi-plus" onClick={onAddGlobal} variant="primary" size="small" />
    </div>
  );
};

// ─── Main ModernArrangementsView ────────────────────────────────────────────────

interface ModernArrangementsViewProps {
  periods:             ArrangementPeriod[];
  selectedDay:         DayOfWeek;
  fadeIn:              boolean;
  arrangements:        TeacherArrangement[];
  arrangementsLoading: boolean;
  arrangementsError:   string | null;
  timetableData:       ClassTimetable[];
  addArrangement:      (payload: AddArrangementPayload) => Promise<{ success: boolean; message: string }>;
  deleteArrangement:   (id: string) => Promise<{ success: boolean; message: string }>;
}

const ModernArrangementsView: React.FC<ModernArrangementsViewProps> = ({
  periods, selectedDay, fadeIn,
  arrangements, arrangementsLoading, arrangementsError,
  timetableData,
  addArrangement, deleteArrangement,
}) => {
  const toast = useRef<Toast>(null);
  const [modalVisible,    setModalVisible]    = useState(false);
  const [prefilledAbsent, setPrefilledAbsent] = useState<string | null>(null);
  const [prefilledRow,    setPrefilledRow]    = useState<ArrangementPeriod | null>(null);
  const [submitting,      setSubmitting]      = useState(false);
  const [deletingId,      setDeletingId]      = useState<string | null>(null);
  const [deleteTarget,    setDeleteTarget]    = useState<TeacherArrangement | null>(null);

  const handleAddPress = (absentName: string | null, row: ArrangementPeriod) => {
    setPrefilledAbsent(absentName);
    setPrefilledRow(row);
    setModalVisible(true);
  };

  const handleSubmit = async (payload: AddArrangementPayload) => {
    setSubmitting(true);
    const result = await addArrangement(payload);
    setSubmitting(false);
    if (result.success) {
      setModalVisible(false);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: result.message, life: 3500 });
    } else {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message, life: 5000 });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    const result = await deleteArrangement(deleteTarget.id);
    setDeletingId(null);
    setDeleteTarget(null);
    if (!result.success) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message, life: 5000 });
    } else {
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: result.message, life: 3000 });
    }
  };

  return (
    <>
      <style>{`
        .mav-fade { opacity: 0; transform: translateY(14px); transition: opacity 0.5s ease, transform 0.5s ease; }
        .mav-fade.mav-fade-in { opacity: 1; transform: translateY(0); }
        .mav-card { background: ${C.cardBg}; border-radius: 16px; border: 1px solid ${C.border}; overflow: hidden; }
        .mav-hint { display: flex; align-items: center; gap: 5px; padding: 10px 14px; border-bottom: 1px solid ${C.border}; background: #FAFBFC; font-size: 12px; color: ${C.textLight}; }
        .mav-row { display: flex; min-height: 80px; }
        .mav-period-cell { width: 78px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px 4px; }
        .mav-divider { width: 1px; background: ${C.border}; }
        .mav-col { flex: 1; min-width: 150px; display: flex; flex-direction: column; padding: 8px 6px; }
        .mav-live-dot { width: 7px; height: 7px; border-radius: 3.5px; background: ${C.live}; display: inline-block; animation: mav-blink 1.2s ease-in-out infinite; }
        @keyframes mav-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }
        .mav-load-row { display: flex; align-items: center; gap: 8px; padding: 12px; background: ${C.cardBg}; border-radius: 10px; border: 1px solid ${C.border}; }
        .mav-error-row { display: flex; align-items: center; gap: 8px; padding: 12px; background: ${C.absentBg}; border-radius: 10px; border: 1px solid ${C.absentBorder}; }
      `}</style>
      <Toast ref={toast} position="top-right" />

      <div className={`mav-fade ${fadeIn ? 'mav-fade-in' : ''}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SummaryBar
          periods={periods}
          totalArranged={arrangements.length}
          onAddGlobal={() => { setPrefilledAbsent(null); setPrefilledRow(null); setModalVisible(true); }}
        />

        {arrangementsLoading && (
          <div className="mav-load-row">
            <i className="pi pi-spin pi-spinner" style={{ color: C.affected }} />
            <span style={{ color: C.textLight, fontSize: 12.5 }}>Loading arrangements…</span>
          </div>
        )}
        {arrangementsError && !arrangementsLoading && (
          <div className="mav-error-row">
            <i className="pi pi-exclamation-triangle" style={{ color: C.absent, fontSize: 13 }} />
            <span style={{ color: C.absent, fontSize: 12.5 }}>{arrangementsError}</span>
          </div>
        )}

        <div className="mav-card">
          <div className="mav-hint">
            <i className="pi pi-info-circle" style={{ fontSize: 12 }} />
            <span>Absent = not checked in · Click + to arrange a replacement</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 700 }}>
              <TableHeader />
              {periods.map((row, i) => (
                <ArrangementRow
                  key={row.period}
                  row={row}
                  isLast={i === periods.length - 1}
                  savedArrangements={arrangements}
                  onAddPress={handleAddPress}
                  onDeleteArrangement={(a) => setDeleteTarget(a)}
                  deletingId={deletingId}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <AddArrangementModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        timetableData={timetableData}
        periodRow={prefilledRow}
        absentTeacherName={prefilledAbsent}
      />

      {/* ── Delete confirmation ── */}
      <Dialog
        visible={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        style={{ width: '90vw', maxWidth: '420px', borderRadius: '20px', overflow: 'hidden' }}
        showHeader={false}
        contentStyle={{ padding: '28px', borderRadius: '20px' }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
      >
        {deleteTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-trash" style={{ fontSize: 22, color: '#dc2626' }} />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1e293b' }}>Delete Arrangement?</div>
            <div style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.6 }}>
              <strong>{deleteTarget.arranged_teacher_name}</strong> will no longer cover{' '}
              {deleteTarget.class_name}{deleteTarget.section ? ` · ${deleteTarget.section}` : ''} — {deleteTarget.subject_name}.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8, width: '100%' }}>
              <GlobalButton label="Cancel" onClick={() => setDeleteTarget(null)} variant="secondary" size="medium" disabled={!!deletingId} fullWidth />
              <GlobalButton
                label={deletingId ? 'Deleting...' : 'Delete'}
                onClick={handleConfirmDelete}
                variant="danger"
                size="medium"
                disabled={!!deletingId}
                loading={!!deletingId}
                fullWidth
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ModernArrangementsView;
